import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";

import { useData } from "../../../storage/accessors";
import SuccessSignsWidget from "../../../components/lifepart/SuccessSignsWidget";
import RoadMapWidget from "../../../components/lifepart/RoadMapWidget";
import LevelUpModal from "../../../components/lifepart/LevelUpModal";
import { useTaskModal } from "../../../components/TaskModalContext";
import ColorPicker from "../../../components/lifepart/ColorPicker";
import Icon from "../../../components/Icon";
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import useWheelId from "../../../hooks/useWheelId";
import { LifePart } from "../../../state/LifePart";
import { useReadonly } from "@/contexts/readonlyContext";

export default function LifePartDetailPage() {
    const { lifePartUuid } = useParams<{ lifePartUuid: string }>();
    const navigate = useNavigate();
    const wheelId = useWheelId();
    const { roles, wheels } = useData();
    const { readonly } = useReadonly();
    const taskModalContext = useTaskModal();
    const setTaskModal = taskModalContext?.setTaskModal;

    const currentPart = lifePartUuid ? roles.objects[lifePartUuid] : undefined;
    const isFocused = wheels.objects['wheel']?.currentLifePart === lifePartUuid;

    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(currentPart?.name || "");
    const [description, setDescription] = useState(currentPart?.description || "");
    const [color, setColor] = useState(currentPart?.color || "#000000");
    const [startpoint, setStartpoint] = useState(currentPart?.startpoint || 0);
    const [successSigns, setSuccessSigns] = useState<LifePart["successSigns"]>(currentPart?.successSigns || []);
    const inputRef = useRef<HTMLInputElement>(null);

    // Состояние для модалки повышения уровня
    const [showLevelUpModal, setShowLevelUpModal] = useState(false);
    const [oldLevel, setOldLevel] = useState(0);
    const [newLevel, setNewLevel] = useState(0);

    useEffect(() => {
        if (currentPart) {
            setName(currentPart.name);
            setDescription(currentPart.description || "");
            setColor(currentPart.color);
            setStartpoint(currentPart.startpoint || 0);
            setSuccessSigns(currentPart.successSigns || []);
        }
    }, [currentPart?.uuid]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const save = (updates: Partial<LifePart>) => {
        if (!lifePartUuid || !currentPart) return;
        roles.save(lifePartUuid, updates);
    };

    const handleOpenTask = (taskUuid: string) => {
        if (setTaskModal) {
            setTaskModal({ taskUUID: taskUuid });
        }
    };

    const handleUpgrade = (step: number) => {
        if (readonly) return;
        setOldLevel(startpoint);
        setNewLevel(step);
        setShowLevelUpModal(true);
        setStartpoint(step);
        save({ startpoint: step });
    };

    const handleEdit = () => {
        if (readonly) return;
        setIsEditing(true);
    };

    const handleSave = () => {
        if (readonly) return;
        save({ name, description, color, startpoint, successSigns });
        setIsEditing(false);
    };

    const handleCancel = () => {
        if (currentPart) {
            setName(currentPart.name);
            setDescription(currentPart.description || "");
            setColor(currentPart.color);
            setStartpoint(currentPart.startpoint || 0);
            setSuccessSigns(currentPart.successSigns || []);
        }
        setIsEditing(false);
    };

    const toggleFocus = () => {
        if (readonly) return;
        wheels.save('wheel', { currentLifePart: isFocused ? null : lifePartUuid });
    };

    if (!currentPart) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">
                    <strong>Роль не найдена</strong>
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(`/${wheelId}/roles`)}>
                    Назад к списку
                </Button>
            </Container>
        );
    }

    const getDotColor = (value: number): string => {
        if (value <= 2) return '#dc3545';
        if (value <= 4) return '#fd7e14';
        if (value <= 6) return '#ffc107';
        if (value <= 8) return '#198754';
        return '#0d6efd';
    };

    // Helper to convert hex to rgba
    const hexToRgba = (hex: string, alpha: number): string => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return (
        <Container className="mt-4">
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(`/${wheelId}/roles`)}
                className="mb-3"
            >
                <Icon icon={faArrowLeft} className="me-2" />
                Назад к списку
            </Button>
            <Card className="border-2 shadow-sm" style={{ borderColor: color }}>
                <Card.Header
                    className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2"
                    style={{ backgroundColor: hexToRgba(color, 0.08) }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className="d-inline-block rounded"
                            style={{
                                width: 20,
                                height: 20,
                                backgroundColor: color,
                                border: "1px solid var(--bs-border-color)"
                            }}
                        />
                        <strong style={{ color }}>
                            {isEditing ? "Редактировать" : (name || "Без названия")}
                        </strong>
                    </div>
                    {!isEditing && (
                        <Button disabled={readonly} variant="outline-primary" size="sm" onClick={handleEdit}>
                            Редактировать
                        </Button>
                    )}
                </Card.Header>

                <Card.Body>
                    <Form>
                        {isEditing && (
                            <Form.Group as={Row}>
                                <Col md={4}>
                                    <Form.Label><strong>Название</strong></Form.Label>
                                </Col>
                                <Col md={8}>
                                    <Form.Control
                                        type="text"
                                        value={name}
                                        onChange={(e: any) => setName(e.target.value)}
                                        ref={inputRef}
                                    />
                                </Col>
                            </Form.Group>
                        )}

                        {(isEditing || description) && (
                            <Form.Group as={Row} className="mt-4">
                                {isEditing && (
                                    <Col md={4}>
                                        <Form.Label><strong>Описание</strong></Form.Label>
                                    </Col>
                                )}
                                <Col md={isEditing ? 8 : 12}>
                                    {isEditing ? (
                                        <Form.Control
                                            as="textarea"
                                            style={{ height: 150, fontSize: "small" }}
                                            value={description}
                                            onChange={(e: any) => setDescription(e.target.value)}
                                        />
                                    ) : (
                                        <Alert variant="secondary" className="py-2 px-3 mb-0">
                                            <div style={{ whiteSpace: "pre-wrap" }}>
                                                {description}
                                            </div>
                                        </Alert>
                                    )}
                                </Col>
                            </Form.Group>
                        )}

                        <Form.Group as={Row} className="mt-4">
                            <Col md={4}>
                                <Form.Label><strong>Цвет</strong></Form.Label>
                            </Col>
                            <Col md={8}>
                                {isEditing ? (
                                    <ColorPicker
                                        value={color}
                                        onChange={(c: string) => setColor(c)}
                                    />
                                ) : (
                                    <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
                                        <span
                                            className="d-inline-block rounded"
                                            style={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: color,
                                                border: "1px solid var(--bs-border-color)"
                                            }}
                                        />
                                    </div>
                                )}
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mt-4">
                            <Col md={4}>
                                <Form.Label>
                                    <strong>Оценка</strong>
                                    <br />
                                    <small>Ваша текущая удовлетворенность от 1 до 10</small>
                                </Form.Label>
                            </Col>
                            <Col md={8}>
                                {isEditing ? (
                                    <div className="d-flex gap-1">
                                        {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                                            <Button
                                                key={value}
                                                variant={value <= startpoint ? "primary" : "outline-primary"}
                                                size="sm"
                                                onClick={() => setStartpoint(value)}
                                                style={{ flex: 1 }}
                                            >
                                                {value}
                                            </Button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center gap-2" style={{ minHeight: 38 }}>
                                        <div className="d-flex gap-1">
                                            {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                                                <span
                                                    key={value}
                                                    style={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: value <= startpoint ? getDotColor(startpoint) : 'var(--bs-border-color)',
                                                        transition: 'all 0.2s ease',
                                                        display: 'inline-block',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        {startpoint > 0 && (
                                            <Badge
                                                style={{
                                                    backgroundColor: getDotColor(startpoint),
                                                    color: startpoint <= 6 ? '#000' : '#fff',
                                                }}
                                            >
                                                {startpoint}
                                            </Badge>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Form.Group>

                        {isEditing && (
                            <div className="mt-4 d-flex gap-2 justify-content-end">
                                <Button variant="primary" size="sm" onClick={handleSave}>
                                    Сохранить
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={handleCancel}>
                                    Отменить
                                </Button>
                            </div>
                        )}
                    </Form>
                </Card.Body>
            </Card>

            <div className="mt-4">
                <SuccessSignsWidget
                    successSigns={successSigns || []}
                    onChange={(signs) => {
                        setSuccessSigns(signs);
                        save({ successSigns: signs });
                    }}
                />
            </div>

            {successSigns && successSigns.length > 0 && (
                <div className="mt-4">
                    <RoadMapWidget
                        lifepartUuid={lifePartUuid!}
                        currentStartpoint={startpoint}
                        onUpgrade={handleUpgrade}
                        onOpenTask={handleOpenTask}
                        isFocused={isFocused}
                        onToggleFocus={toggleFocus}
                        showNavigateButton={false}
                    />
                </div>
            )}

            <LevelUpModal
                show={showLevelUpModal}
                onHide={() => setShowLevelUpModal(false)}
                lifePart={currentPart}
                oldLevel={oldLevel}
                newLevel={newLevel}
            />
        </Container>
    );
}